/**
 * API client with auth support.
 * Use `api.get(path)` for public/unauthed calls.
 * Frontend components should use `useAuth().authedFetch()` for authed calls.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  async request(path, options = {}) {
    const url = `${this.baseUrl}${path}`;
    const config = {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    };
    const res = await fetch(url, config);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.detail || err.message || `Request failed: ${res.status}`);
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    }
    return res;
  }

  async authRequest(path, options = {}, token) {
    const url = `${this.baseUrl}${path}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };
    const res = await fetch(url, config);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.detail || err.message || `Request failed: ${res.status}`);
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    }
    return res;
  }

  get(path) { return this.request(path); }
  post(path, data) { return this.request(path, { method: 'POST', body: JSON.stringify(data) }); }
  put(path, data) { return this.request(path, { method: 'PUT', body: JSON.stringify(data) }); }
  patch(path, data) { return this.request(path, { method: 'PATCH', body: JSON.stringify(data) }); }
  delete(path) { return this.request(path, { method: 'DELETE' }); }

  getWsUrl(path) {
    const base = this.baseUrl.replace(/^http/, 'ws');
    return `${base}${path}`;
  }

  getDownloadUrl(path) {
    return `${this.baseUrl}${path}`;
  }
}

export const api = new ApiClient(API_BASE);
